import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getActingUser, setActingUser } from './api';
import type { DevUser, Me } from './types';

/**
 * Session context.
 *
 * `me` carries the capability map the server computed, so the UI can hide
 * controls it would be refused. The server remains the authority — this only
 * avoids offering dead ends.
 */

interface SessionValue {
  me: Me;
  devUsers: DevUser[];
  switchUser(email: string): void;
  isDevMode: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}

/** Convenience: capability lookup without destructuring `me` everywhere. */
export function useCan(): Me['capabilities'] {
  return useSession().me.capabilities;
}

export function SessionProvider({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: (state: { loading: boolean; error: unknown; retry: () => void }) => ReactNode;
}) {
  const queryClient = useQueryClient();

  /**
   * The acting user is React state, not just a localStorage read.
   *
   * localStorage is what the API client sends on each request, but it is not
   * reactive — writing to it renders nothing and changes no query key. Holding it
   * in state is what makes a switch actually take effect: the `me` key changes,
   * so React Query fetches the new person rather than sitting on the old result.
   */
  const [actingUser, setActingUserState] = useState<string | null>(() => getActingUser());

  const devUsers = useQuery({
    queryKey: ['dev-users'],
    queryFn: () => api.get<{ data: DevUser[] }>('/dev/users'),
    // Absent in SSO mode; that is expected, not an error worth surfacing.
    retry: false,
    staleTime: Infinity,
  });

  const me = useQuery({
    queryKey: ['me', actingUser],
    queryFn: () => api.get<Me>('/me'),
    retry: false,
  });

  const switchUser = useCallback(
    (email: string) => {
      if (email === actingUser) return;
      // Order matters: the header must already be right before anything refetches.
      setActingUser(email);
      setActingUserState(email);
      /*
       * Every other query is scoped to the acting user but keyed without them, so
       * they have to be reset explicitly. `resetQueries` (not `clear`) is the
       * one that refetches active observers — `clear` only empties the cache and
       * leaves what is on screen untouched, which is exactly the bug this
       * replaces.
       */
      void queryClient.resetQueries();
    },
    [actingUser, queryClient],
  );

  if (me.isLoading) return <>{fallback({ loading: true, error: null, retry: () => me.refetch() })}</>;
  if (me.error || !me.data) {
    return <>{fallback({ loading: false, error: me.error, retry: () => me.refetch() })}</>;
  }

  const value: SessionValue = {
    me: me.data,
    devUsers: devUsers.data?.data ?? [],
    isDevMode: Boolean(devUsers.data),
    switchUser,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
